// --- Dependencies -------------------------------------------------------------------------------
import {
    ArcElement,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Filler,
    Legend,
    LineElement,
    LinearScale,
    PointElement,
    Tooltip,
    type ChartData,
    type ChartOptions,
} from "chart.js";
import { useMemo } from "react";
import { Bar, Doughnut, Line } from "react-chartjs-2";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import constants from "@/config/constants";
import { buildDataset, buildOptions, readChartTokens, type TyChartColor, type TyChartJsKind, type TyChartKind } from "./presets";
import "./styles.css";
// --- Types --------------------------------------------------------------------------------------
export type TyChartSeries = {
    label: string;
    data: number[];
    color: TyChartColor;
};

type Props = {
    kind: TyChartKind;
    labels: string[];
    series: TyChartSeries[];
    /** Shown instead of an empty frame — a chart with no bars reads as broken, not as "nothing yet". */
    emptyLabel: string;
    /** Plot height, owned by the stylesheet — the three the dashboard's grid actually uses. */
    size?: "sm" | "md" | "lg";
    /** How a value is spelled everywhere it appears: axis ticks, tooltips, and the screen-reader table. */
    formatValue?: (value: number) => string;
    /** Names what the chart shows, for the table a screen reader reads instead of the canvas. */
    caption: string;
    /**
     * One named slot per category rather than per series. A doughnut needs it because every slice is
     * its own category, and a single-series bar chart wants it whenever the categories mean different
     * things — the pipeline's won and lost must not read like its working stages.
     */
    itemColors?: TyChartColor[];
};
// --- Logic --------------------------------------------------------------------------------------
/**
 * Tree-shaken on purpose: chart.js ships every controller and scale it has, and importing the whole
 * bundle would cost more than the rest of the dashboard put together. Registering here means a new
 * chart type needs its element added — which is the reminder that it also needs a preset.
 */
ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Filler, Legend, Tooltip);

/** Western digits in both languages, for the same reason `formatMoney` uses them — see `utils`. */
const DEFAULT_FORMAT = (value: number) => new Intl.NumberFormat("en-US").format(value);

/**
 * The one chart wrapper for the dashboard. Every card goes through it so the grid weight, tick size,
 * tooltip and palette are decided once — six cards each configuring chart.js on their own is how a
 * dashboard ends up looking like six dashboards.
 *
 * The canvas is a picture with no text in the accessibility tree, so each chart also renders its own
 * numbers as a visually hidden table. That is the table view the marks alone cannot provide.
 */
export default function Chart({ kind, labels, series, emptyLabel, size = "md", formatValue = DEFAULT_FORMAT, caption, itemColors }: Props) {
    const lang = useLang((state) => state.lang) ?? "ar";
    const isRtl = constants.rtlLanguages.includes(lang as "ar");

    const tokens = useMemo(() => readChartTokens(), []);

    const isEmpty = !labels.length || series.every((one) => one.data.every((value) => !value));

    const data = useMemo<ChartData<TyChartJsKind>>(() => {
        /**
         * Resolved here, once, so a slice list and a series list cannot drift into two colour schemes.
         *
         * A doughnut is one dataset whose *slices* are the categories, so without a colour per slice
         * chart.js paints the whole ring in the dataset's single colour. Bars take the same array,
         * which is how the pipeline tints its terminal stages differently from the rest.
         */
        const items = itemColors?.map((name) => tokens.byName[name]);

        return {
            labels,
            datasets: series.map((one) => buildDataset(kind, one.label, one.data, tokens.byName[one.color], tokens, items)),
        };
    }, [kind, labels, series, itemColors, tokens]);

    /** A single series needs no legend box — the card's own heading already names it. */
    const options = useMemo(
        () => buildOptions(kind, tokens, isRtl, formatValue, series.length > 1),
        [kind, tokens, isRtl, formatValue, series.length]
    );

    if (isEmpty) return <p className="chart-empty">{emptyLabel}</p>;

    return (
        <div className="chart">
            {/* The canvas keeps its own direction: a mirrored time axis would put the most recent
                month where an Arabic reader expects the oldest. Legend and tooltip text still flip. */}
            <div className="chart-canvas" data-size={size} dir="ltr">
                {/* One options object covers all three controllers, so each render narrows it back
                    to the one it is actually handing to chart.js. */}
                {kind === "area" && <Line data={data as ChartData<"line">} options={options as ChartOptions<"line">} />}
                {kind === "doughnut" && <Doughnut data={data as ChartData<"doughnut">} options={options as ChartOptions<"doughnut">} />}
                {(kind === "bar" || kind === "stackedBar" || kind === "hbar") && (
                    <Bar data={data as ChartData<"bar">} options={options as ChartOptions<"bar">} />
                )}
            </div>

            <table className="chart-table">
                <caption>{caption}</caption>
                <thead>
                    <tr>
                        <th scope="col">{caption}</th>
                        {series.map((one) => (
                            <th key={one.label} scope="col">
                                {one.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {labels.map((label, index) => (
                        <tr key={`${label}-${index}`}>
                            <th scope="row">{label}</th>
                            {series.map((one) => (
                                <td key={one.label}>{formatValue(one.data[index] ?? 0)}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
